import { store } from './store';

// Safe mode and crash-loop detection, decided before anything heavy starts (device.ts reads it for its profile).
//
// Each tab keeps a small run marker in sessionStorage: "running" while the page is open and visible, "away" once it is
// hidden or closed. A tab the browser killed (out of memory, a GPU hang: the "this page crashed / a problem repeatedly
// occurred" reload on phones) never gets to write "away", so the next start in that tab finds "running" and knows the
// last run died, and in which stage (studio, city loading, driving). Two such deaths in a row start the tab in safe mode:
// lower pixel density, no bloom or shadows, smaller textures. A clean run (healthy and then hidden) resets the count.
// The visitor can also choose safe mode (kept until they turn it off), or force it with ?safe=1 / ?safe=0.

type Run = { state: 'running' | 'away'; stage: string; fails: number; healthy: boolean };
const KEY = 'sd.run';
function read(): Run { try { return JSON.parse(sessionStorage.getItem(KEY) ?? '') as Run; } catch { return { state: 'away', stage: 'boot', fails: 0, healthy: false }; } }
function write(r: Run) { try { sessionStorage.setItem(KEY, JSON.stringify(r)); } catch { /* storage blocked */ } }

const last = read();
const crashed = last.state === 'running';
const fails = crashed ? last.fails + 1 : last.healthy ? 0 : last.fails;
const run: Run = { state: 'running', stage: 'boot', fails, healthy: false };
write(run);

const param = new URLSearchParams(location.search).get('safe');
if (param === '1' || param === '0') store.set('safe', param === '1' ? '1' : null);
export const SAFE = {
  /** safe mode is on for this run */
  on: param === '1' || (param !== '0' && (store.get('safe') === '1' || fails >= 2)),
  /** turned on automatically after repeated crashes (not the visitor's choice) */
  auto: param !== '1' && store.get('safe') !== '1' && fails >= 2,
  /** the previous run in this tab ended without closing: where it was */
  lastCrash: crashed ? last.stage : null,
  fails,
};
if (SAFE.on) document.documentElement.classList.add('safe-mode');

/** Record where the app is (boot, studio, city-loading, city…), so a crash report says where it died. */
export function stage(name: string) { run.stage = name; write(run); }
/** The app has run for a while without trouble: the crash count resets when this run ends cleanly. */
export function healthy() { if (!run.healthy) { run.healthy = true; write(run); } }
export function setSafeMode(on: boolean) { store.set('safe', on ? '1' : null); }

addEventListener('visibilitychange', () => { run.state = document.hidden ? 'away' : 'running'; if (document.hidden && run.healthy) run.fails = 0; write(run); });
addEventListener('pagehide', () => { run.state = 'away'; if (run.healthy) run.fails = 0; write(run); });
